import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import {blueGrey, indigo} from '@mui/material/colors'
import {useAppNavigate} from 'app/hooks/navigation/useAppNavigate'
import {MOpportunityEntityDetail} from 'app/modules/discovery/core/_models'
import {Link, useLocation} from 'react-router-dom'
import {useTracking} from 'react-tracking'
import {emFormatToCurrency} from 'utilities/extensionMethods'
import HorizontalSquareChips from './HorizontalSquareChips'
import ShowMoreText from './ShowMoreText'

export default function OpportunityResult({
  opportunity,
  scrollPosition,
  search,
  fromDir,
  fromProject,
  page,
  busDetTab,
  showFullOpportunity,
}: {
  opportunity: MOpportunityEntityDetail
  scrollPosition?: number
  search?: string
  fromDir?: boolean
  fromProject?: boolean
  page?: number
  busDetTab?: string
  showFullOpportunity?: (OppId: string) => void
}) {
  const navigate = useAppNavigate()
  const location = useLocation()
  const {trackEvent} = useTracking<{entity: string; id: string}>()
  const route = `/opportunities/detail?opportunityId=${opportunity.id}${
    scrollPosition ? `&scrollPosition=${scrollPosition}` : ''
  }${search || fromDir ? `&fromOpportunityDirectory=${search ? search : 'tru'}` : ''}${
    page ? `&page=${page}` : ''
  }${
    busDetTab
      ? `&retval=${
          location.pathname + location.search?.replaceAll('&', '+')
        }+busDetTab=${busDetTab}`
      : ''
  }`

  return (
    <Card elevation={0} sx={{p: 0, m: 0, mb: 2}}>
      <CardHeader
        sx={{p: 0, m: 0}}
        title={
          <>
            <Box display={'flex'}>
              {fromDir ? (
                <Link
                  className='text-hover-primary'
                  to={route}
                  onClick={() => {
                    trackEvent({entity: 'person', id: `${opportunity.id}`})
                  }}
                  color={indigo[300]}
                  style={{fontWeight: 600, fontSize: '1em', color: indigo[300]}}
                >
                  {opportunity?.name}
                </Link>
              ) : fromProject && showFullOpportunity ? (
                <Typography
                  variant='h6'
                  component='div'
                  fontWeight={600}
                  color={indigo[300]}
                  style={{cursor: 'pointer'}}
                  fontSize={'1em'}
                  onClick={() => opportunity.id && showFullOpportunity(opportunity.id)}
                >
                  {opportunity?.name}
                </Typography>
              ) : (
                <Link
                  className='text-hover-primary'
                  to={`/opportunity/detail?opportunityId=${opportunity.id}`}
                >
                  {opportunity?.name}
                </Link>
              )}
            </Box>
          </>
        }
      />
      <CardContent sx={{pt: 1, pb: '1 !important'}}>
        <Box>
          <List dense={true} sx={{p: 0}}>
            {opportunity?.solicitation_number ? (
              <ListItem sx={{p: 0, pl: 0}}>
                <Typography variant={'h6'} fontWeight={600} color={blueGrey[900]} mr={1}>
                  Notice_id:
                </Typography>
                <Typography variant={'body1'}>{opportunity?.solicitation_number}</Typography>
              </ListItem>
            ) : (
              <></>
            )}
            {opportunity?.amount ? (
              <ListItem sx={{p: 0, pl: 0}}>
                <Typography variant={'h6'} fontWeight={600} color={blueGrey[900]} mr={1}>
                  Amount:
                </Typography>
                <Typography variant={'body1'}>{emFormatToCurrency(opportunity?.amount)}</Typography>
              </ListItem>
            ) : (
              <></>
            )}
            {opportunity?.dept_path ? (
              <ListItem sx={{p: 0, pl: 0}} alignItems='flex-start'>
                <Typography variant={'h6'} fontWeight={600} color={blueGrey[900]} mr={1}>
                  Departments:
                </Typography>
                <HorizontalSquareChips
                  items={opportunity?.dept_path ?? []}
                  size={'small'}
                  maxItems={12}
                  moreString='Show All Depts'
                  lessString='Show Less Depts'
                />
              </ListItem>
            ) : (
              <></>
            )}
          </List>
        </Box>
        <Box display={'flex'} flexDirection={'column'}>
          <Typography variant={'h6'} fontWeight={600} color={blueGrey[900]} mr={1}>
            Description:
          </Typography>
          {opportunity?.description ? (
            <Box sx={{color: blueGrey[400]}}>
              {opportunity?.description.includes('<') ? (
                <ShowMoreText
                  variant={'h6'}
                  maxLen={300}
                  inheritColor={true}
                  text={opportunity.description}
                  isHTML={true}
                />
              ) : (
                <ShowMoreText
                  variant={'h6'}
                  maxLen={300}
                  inheritColor={true}
                  text={opportunity.description}
                  isHTML={false}
                />
              )}
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
